import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { AuthService } from '@scripts/woo_services.module/AuthService';
import { CampaignService } from '@scripts/woo_services.module/CampaignService';
import { Campaign, CampaignStatus, wooId } from '@scripts/woo_services.module/shared-types';

@Injectable({ providedIn: 'root' })
class EditStartedCampaignRedirect {
  private authService = inject(AuthService);
  private campaignService = inject(CampaignService);
  private router = inject(Router);
  // With the OMS toggle on, the legacy edit route forwards to the OMS one while its fetch is still in flight
  private inFlight = new Map<wooId, Promise<Campaign>>();

  async toEditOngoingWhenStarted(route: ActivatedRouteSnapshot): Promise<boolean | RedirectCommand> {
    // AuthGuard/RoleGuard already reject (and notify) these users
    if (!this.authService.isAuthenticated() || !this.authService.hasAnyRole(route.data.roles)) {
      return false;
    }
    const id = route.paramMap.get('id');
    let campaign: Campaign;
    try {
      campaign = await this.fetch(id);
    } catch {
      // The error interceptor already shows the failure; let the edit page handle it as before
      return true;
    }
    if (!this.campaignService.hasAnyStatus(campaign, [CampaignStatus.ongoing, CampaignStatus.ended])) {
      return true;
    }
    const state = this.router.getCurrentNavigation()?.extras.state;
    return new RedirectCommand(this.router.createUrlTree(['/campaigns', id, 'edit_ongoing']), {
      state: { ...state, id },
    });
  }

  private fetch(id: wooId): Promise<Campaign> {
    if (!this.inFlight.has(id)) {
      this.inFlight.set(
        id,
        this.campaignService.get(id).finally(() => this.inFlight.delete(id)),
      );
    }
    return this.inFlight.get(id);
  }
}

// A tab left open since before the campaign started still holds its old status, so the
// dashboard would send it to the full rebooking page. Check the current status here instead.
export const RedirectStartedCampaignToEditOngoingGuard: CanActivateFn = (route) =>
  inject(EditStartedCampaignRedirect).toEditOngoingWhenStarted(route);
